import {
  FormSchema,
  FormSchemaApplicationDetail,
  FormSchemaCase,
  FormSchemaStatus,
} from "pages/ApplicationForm/formSchema";
import { PropertyStepResponse } from "pages/ApplicationForm/steps/Property/types";
import {
  DEFAULT_APPLICANTS_FORM_STATUS,
  DEFAULT_FORM_STATUS,
  getApplicationStatus,
} from "pages/ApplicationForm/formStatus";
import { APPLICATION_IN_PROGRESS_KEY } from "utils/consts";
import { create } from "zustand";

interface FormStore {
  applicationId: string | null;
  applicationDetail: FormSchemaApplicationDetail | null;
  applicationCase: FormSchemaCase | null;
  formData: FormSchema;
  formStatus: FormSchemaStatus;
  brokerDetailPopulated: boolean;
  avmValuation: number | null;
  applicationErrors: { code: string; message: string }[] | null;
  setApplicationErrors: (errors: { code: string; message: string }[]) => void;
  setApplicationId: (applicationId: string | null) => void;
  setApplicationDetail: (application: any) => void;
  setPricingExpiryDate: (pricingExpiryDate: string | null) => void;
  setAppllicationCase: (caseDetail: any) => void;
  setFormData: (
    data: Partial<FormSchema> | PropertyStepResponse,
    storeKey: string,
    numberOfApplicants?: number,
  ) => void;
  setAvmValuation: (
    avmValuation: number | null,
    avmConfidenceLevel: string | null,
  ) => void;
  setFormDataFromExistingApplication: (data: any) => void;
  setFormStatus: (data: any) => void;
  setBrokerDetailPopulated: () => void;
  resetApplicantsFormStatus: () => void;
  resetApplication: () => void;
}

const resolveAvmValuation = (
  avmValuation: number | null | undefined,
  avmConfidenceLevel: string | null | undefined,
): number | null =>
  avmValuation &&
  (avmConfidenceLevel === "MEDIUM" || avmConfidenceLevel === "HIGH")
    ? avmValuation
    : null;

const useApplicationStore = create<FormStore>((set) => ({
  applicationId: null,
  applicationDetail: null,
  applicationCase: null,
  formData: {
    numberOfApplicants: 1,
  },
  formStatus: DEFAULT_FORM_STATUS,
  brokerDetailPopulated: false,
  avmValuation: null,
  applicationErrors: null,

  setApplicationErrors: (errors) => set({ applicationErrors: errors }),

  setApplicationId: (applicationId) =>
    set((state) => {
      if (!state.applicationId && applicationId) {
        return {
          applicationId: applicationId,
        };
      }
      return {
        applicationId: state.applicationId,
      };
    }),

  setApplicationDetail: (application) =>
    set((state) => ({
      applicationDetail: {
        reference: application?.reference || state.applicationDetail?.reference,
        modifiedOn:
          application?.modifiedOn || state.applicationDetail?.modifiedOn,
        // Preserve the current expiry when a response omits it; null is intentional.
        pricingExpiryDate:
          application?.pricingExpiryDate !== undefined
            ? application.pricingExpiryDate
            : state.applicationDetail?.pricingExpiryDate,
      },
    })),

  setPricingExpiryDate: (pricingExpiryDate) =>
    set((state) => ({
      applicationDetail: state.applicationDetail
        ? { ...state.applicationDetail, pricingExpiryDate }
        : state.applicationDetail,
    })),

  setAppllicationCase: (caseDetail) =>
    set((state) => ({
      applicationCase: caseDetail || state.applicationCase,
    })),

  /**
   * Update form data for a given step in the application.
   *
   * When the product step is completed, the selectedProductUuid is set in the data.
   * If the product step is not completed, the selectedProductUuid is set to an empty string.
   * This is to ensure that we force a product selection when the user navigates the form, and keeps
   * data in sync with the matching backend logic.
   *
   * @param data - The data to set in the form data.
   * @param storeKey - The key of the store to set the data in (step).
   * @param numberOfApplicants - The number of applicants in the application.
   * @returns The new state of the form data.
   */
  setFormData: (data, storeKey, numberOfApplicants) =>
    set((state) => {
      if (storeKey === "property") {
        const { property, loan } = data as PropertyStepResponse;
        return {
          avmValuation: resolveAvmValuation(
            property?.avmValuation,
            property?.avmConfidenceLevel,
          ),
          formData: {
            ...state.formData,
            property,
            ...(loan && { loan }),
            product: {
              ...state.formData.product,
              selectedProductUuid: "",
              status: APPLICATION_IN_PROGRESS_KEY,
            },
          },
        };
      }

      const formData = data as Partial<FormSchema>;
      return {
        formData: {
          ...state.formData,
          ...(numberOfApplicants && { numberOfApplicants }),
          [storeKey]:
            storeKey === "applicants" ? formData?.applicants : formData,
          ...(!(formData && "selectedProductUuid" in formData)
            ? {
                product: {
                  ...state.formData.product,
                  selectedProductUuid: "",
                  status: APPLICATION_IN_PROGRESS_KEY,
                },
              }
            : {}),
        },
      };
    }),

  setAvmValuation: (avmValuation, avmConfidenceLevel) =>
    set({
      avmValuation: resolveAvmValuation(avmValuation, avmConfidenceLevel),
    }),

  setFormDataFromExistingApplication: (data) =>
    set({
      applicationId: data?.uuid,
      applicationDetail: {
        reference: data?.reference,
        modifiedOn: data?.modifiedOn,
        pricingExpiryDate: data?.pricingExpiryDate,
      },
      applicationCase: data?.case,
      formData: {
        numberOfApplicants: data?.numberOfApplicants,
        broker: data?.broker,
        applicants: data?.applicants,
        employments: data?.employments,
        expenditure: data?.expenditure,
        loan: data?.loan,
        property: data?.property,
        product: data?.product,
        other: data?.other,
      },
      avmValuation: resolveAvmValuation(
        data?.property?.avmValuation,
        data?.property?.avmConfidenceLevel,
      ),
    }),

  setFormStatus: (data) =>
    set({
      formStatus: getApplicationStatus(data),
    }),

  setBrokerDetailPopulated: () =>
    set({
      brokerDetailPopulated: true,
    }),

  resetApplicantsFormStatus: () =>
    set((state) => ({
      formStatus: {
        ...state.formStatus,
        ...DEFAULT_APPLICANTS_FORM_STATUS,
      },
    })),

  resetApplication: () =>
    set({
      applicationId: null,
      applicationDetail: null,
      applicationCase: null,
      formData: {
        numberOfApplicants: 1,
      },
      formStatus: DEFAULT_FORM_STATUS,
      brokerDetailPopulated: false,
      avmValuation: null,
      applicationErrors: null,
    }),
}));

export default useApplicationStore;
